import apiService from './apiService';
import { API_ENDPOINTS } from '../config/api';

class WeddingEventService {
  // ==========================================
  // WEDDING EVENT CRUD
  // ==========================================

  /**
   * Get all wedding events for current user
   */
  async getEvents() {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.LIST);
      return response;
    } catch (error) {
      console.error('Get events error:', error);
      throw error;
    }
  }

  /**
   * Get single wedding event by ID
   */
  async getEvent(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.DETAIL(eventId));
      return response;
    } catch (error) {
      console.error('Get event error:', error);
      throw error;
    }
  }

  /**
   * Create new wedding event
   */
  async createEvent(eventData) {
    try {
      const response = await apiService.post(API_ENDPOINTS.WEDDING_EVENTS.CREATE, eventData);
      return response;
    } catch (error) {
      console.error('Create event error:', error);
      throw error;
    }
  }

  /**
   * Update wedding event
   */
  async updateEvent(eventId, updateData) {
    try {
      const response = await apiService.patch(API_ENDPOINTS.WEDDING_EVENTS.UPDATE(eventId), updateData);
      return response;
    } catch (error) {
      console.error('Update event error:', error);
      throw error;
    }
  }

  /**
   * Delete wedding event
   */
  async deleteEvent(eventId) {
    try {
      const response = await apiService.delete(API_ENDPOINTS.WEDDING_EVENTS.DELETE(eventId));
      return response;
    } catch (error) {
      console.error('Delete event error:', error);
      throw error;
    }
  }

  /**
   * Bemutató esemény visszaállítása a sablon szerinti állapotra (új azonosítót kap)
   */
  async resetDemo() {
    try {
      return await apiService.post(API_ENDPOINTS.WEDDING_EVENTS.DEMO_RESET, {});
    } catch (error) {
      console.error('Reset demo error:', error);
      throw error;
    }
  }

  /**
   * Get event summary (budget, tasks, guests stats)
   */
  async getEventSummary(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.SUMMARY(eventId));
      return response;
    } catch (error) {
      console.error('Get event summary error:', error);
      throw error;
    }
  }

  // ==========================================
  // CATEGORIES
  // ==========================================

  /**
   * Get selected categories for event
   */
  async getEventCategories(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.CATEGORIES(eventId));
      return response;
    } catch (error) {
      console.error('Get event categories error:', error);
      throw error;
    }
  }

  /**
   * Add category to event
   */
  async addCategory(eventId, categoryData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_CATEGORY(eventId),
        categoryData
      );
      return response;
    } catch (error) {
      console.error('Add category error:', error);
      throw error;
    }
  }

  /**
   * Remove category from event
   */
  async removeCategory(eventId, categoryId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.REMOVE_CATEGORY(eventId, categoryId)
      );
      return response;
    } catch (error) {
      console.error('Remove category error:', error);
      throw error;
    }
  }

  // ==========================================
  // GUESTS
  // ==========================================

  /**
   * Get all guests for event
   */
  async getGuests(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.GUESTS(eventId));
      return response;
    } catch (error) {
      console.error('Get guests error:', error);
      throw error;
    }
  }

  /**
   * Add guest to event
   */
  async addGuest(eventId, guestData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_GUEST(eventId),
        guestData
      );
      return response;
    } catch (error) {
      console.error('Add guest error:', error);
      throw error;
    }
  }

  /**
   * Update guest
   */
  async updateGuest(eventId, guestId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_GUEST(eventId, guestId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update guest error:', error);
      throw error;
    }
  }

  /**
   * Delete guest
   */
  async deleteGuest(eventId, guestId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_GUEST(eventId, guestId)
      );
      return response;
    } catch (error) {
      console.error('Delete guest error:', error);
      throw error;
    }
  }

  /**
   * Bulk import guests
   */
  async importGuests(eventId, guestsArray) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.IMPORT_GUESTS(eventId),
        { guests: guestsArray }
      );
      return response;
    } catch (error) {
      console.error('Import guests error:', error);
      throw error;
    }
  }

  // ==========================================
  // BUDGET
  // ==========================================

  /**
   * Get budget summary
   */
  async getBudgetSummary(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.BUDGET_SUMMARY(eventId));
      return response;
    } catch (error) {
      console.error('Get budget summary error:', error);
      throw error;
    }
  }

  /**
   * Get all budget items
   */
  async getBudgetItems(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.BUDGET_ITEMS(eventId));
      return response;
    } catch (error) {
      console.error('Get budget items error:', error);
      throw error;
    }
  }

  /**
   * Add budget item
   */
  async addBudgetItem(eventId, itemData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_BUDGET_ITEM(eventId),
        itemData
      );
      return response;
    } catch (error) {
      console.error('Add budget item error:', error);
      throw error;
    }
  }

  /**
   * Update budget item
   */
  async updateBudgetItem(eventId, itemId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_BUDGET_ITEM(eventId, itemId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update budget item error:', error);
      throw error;
    }
  }

  /**
   * Delete budget item
   */
  async deleteBudgetItem(eventId, itemId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_BUDGET_ITEM(eventId, itemId)
      );
      return response;
    } catch (error) {
      console.error('Delete budget item error:', error);
      throw error;
    }
  }

  async getVendorCategories(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.VENDOR_CATEGORIES(eventId));
      return response;
    } catch (error) {
      console.error('Get vendor categories error:', error);
      throw error;
    }
  }

  // ==========================================
  // VENDORS
  // ==========================================

  /**
   * Get combined vendors (marketplace bookings + custom)
   */
  async getVendors(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.VENDORS(eventId));
      return response;
    } catch (error) {
      console.error('Get vendors error:', error);
      throw error;
    }
  }

  /**
   * Add custom vendor
   */
  async addCustomVendor(eventId, vendorData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_CUSTOM_VENDOR(eventId),
        vendorData
      );
      return response;
    } catch (error) {
      console.error('Add custom vendor error:', error);
      throw error;
    }
  }

  /**
   * Update custom vendor
   */
  async updateCustomVendor(eventId, vendorId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_CUSTOM_VENDOR(eventId, vendorId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update custom vendor error:', error);
      throw error;
    }
  }

  /**
   * Delete custom vendor
   */
  async deleteCustomVendor(eventId, vendorId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_CUSTOM_VENDOR(eventId, vendorId)
      );
      return response;
    } catch (error) {
      console.error('Delete custom vendor error:', error);
      throw error;
    }
  }

  // ==========================================
  // TASKS
  // ==========================================

  /**
   * Get all tasks
   */
  async getTasks(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.TASKS(eventId));
      return response;
    } catch (error) {
      console.error('Get tasks error:', error);
      throw error;
    }
  }

  /**
   * Add task
   */
  async addTask(eventId, taskData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_TASK(eventId),
        taskData
      );
      return response;
    } catch (error) {
      console.error('Add task error:', error);
      throw error;
    }
  }

  /**
   * Update task
   */
  async updateTask(eventId, taskId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_TASK(eventId, taskId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update task error:', error);
      throw error;
    }
  }

  /**
   * Toggle task completion
   */
  async toggleTask(eventId, taskId) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.TOGGLE_TASK(eventId, taskId)
      );
      return response;
    } catch (error) {
      console.error('Toggle task error:', error);
      throw error;
    }
  }

  /**
   * Delete task
   */
  async deleteTask(eventId, taskId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_TASK(eventId, taskId)
      );
      return response;
    } catch (error) {
      console.error('Delete task error:', error);
      throw error;
    }
  }

  // ==========================================
  // TIMELINE
  // ==========================================

  /**
   * Get timeline events
   */
  async getTimeline(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.TIMELINE(eventId));
      return response;
    } catch (error) {
      console.error('Get timeline error:', error);
      throw error;
    }
  }

  /**
   * Add timeline event
   */
  async addTimelineEvent(eventId, eventData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_TIMELINE_EVENT(eventId),
        eventData
      );
      return response;
    } catch (error) {
      console.error('Add timeline event error:', error);
      throw error;
    }
  }

  /**
   * Update timeline event
   */
  async updateTimelineEvent(eventId, timelineEventId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_TIMELINE_EVENT(eventId, timelineEventId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update timeline event error:', error);
      throw error;
    }
  }

  /**
   * Delete timeline event
   */
  async deleteTimelineEvent(eventId, timelineEventId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_TIMELINE_EVENT(eventId, timelineEventId)
      );
      return response;
    } catch (error) {
      console.error('Delete timeline event error:', error);
      throw error;
    }
  }

  /**
   * Reorder timeline events
   */
  async reorderTimeline(eventId, eventOrder) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.REORDER_TIMELINE(eventId),
        { eventOrder }
      );
      return response;
    } catch (error) {
      console.error('Reorder timeline error:', error);
      throw error;
    }
  }

  // ==========================================
  // SEATING
  // ==========================================

  /**
   * Get seating tables
   */
  async getSeatingTables(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.SEATING_TABLES(eventId));
      return response;
    } catch (error) {
      console.error('Get seating tables error:', error);
      throw error;
    }
  }

  /**
   * Add seating table
   */
  async addSeatingTable(eventId, tableData) {
    try {
      const response = await apiService.post(
        API_ENDPOINTS.WEDDING_EVENTS.ADD_SEATING_TABLE(eventId),
        tableData
      );
      return response;
    } catch (error) {
      console.error('Add seating table error:', error);
      throw error;
    }
  }

  /**
   * Update seating table
   */
  async updateSeatingTable(eventId, tableId, updateData) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_SEATING_TABLE(eventId, tableId),
        updateData
      );
      return response;
    } catch (error) {
      console.error('Update seating table error:', error);
      throw error;
    }
  }

  /**
   * Delete seating table
   */
  async deleteSeatingTable(eventId, tableId) {
    try {
      const response = await apiService.delete(
        API_ENDPOINTS.WEDDING_EVENTS.DELETE_SEATING_TABLE(eventId, tableId)
      );
      return response;
    } catch (error) {
      console.error('Delete seating table error:', error);
      throw error;
    }
  }

  /**
   * Get seating layout JSON
   */
  async getSeatingLayout(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.SEATING_LAYOUT(eventId));
      return response;
    } catch (error) {
      console.error('Get seating layout error:', error);
      throw error;
    }
  }

  /**
   * Save seating layout JSON (full replace)
   */
  async saveSeatingLayout(eventId, layout) {
    try {
      const response = await apiService.put(
        API_ENDPOINTS.WEDDING_EVENTS.SAVE_SEATING_LAYOUT(eventId),
        layout
      );
      return response;
    } catch (error) {
      console.error('Save seating layout error:', error);
      throw error;
    }
  }

  /**
   * Update guest category
   */
  async updateGuestCategory(eventId, guestId, guestCategory) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UPDATE_GUEST(eventId, guestId),
        { guestCategory }
      );
      return response;
    } catch (error) {
      console.error('Update guest category error:', error);
      throw error;
    }
  }

  /**
   * Assign guest to table (with optional seat number)
   */
  async assignGuestToTable(eventId, guestId, tableId, seatNumber = null) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.ASSIGN_GUEST_TO_TABLE(eventId, guestId),
        { tableId, seatNumber }
      );
      return response;
    } catch (error) {
      console.error('Assign guest to table error:', error);
      throw error;
    }
  }

  /**
   * Unassign guest from table
   */
  async unassignGuestFromTable(eventId, guestId) {
    try {
      const response = await apiService.patch(
        API_ENDPOINTS.WEDDING_EVENTS.UNASSIGN_GUEST_FROM_TABLE(eventId, guestId)
      );
      return response;
    } catch (error) {
      console.error('Unassign guest from table error:', error);
      throw error;
    }
  }

  /**
   * Get seating chart (all tables with guests)
   */
  async getSeatingChart(eventId) {
    try {
      const response = await apiService.get(API_ENDPOINTS.WEDDING_EVENTS.SEATING_CHART(eventId));
      return response;
    } catch (error) {
      console.error('Get seating chart error:', error);
      throw error;
    }
  }

  // ==========================================
  // INVITATION
  // ==========================================

  async getInvitation(eventId) {
    return apiService.get(API_ENDPOINTS.WEDDING_EVENTS.INVITATION(eventId));
  }

  async saveInvitationSettings(eventId, data) {
    return apiService.post(API_ENDPOINTS.WEDDING_EVENTS.INVITATION(eventId), data);
  }

  async uploadInvitationImage(eventId, file) {
    const formData = new FormData();
    formData.append('invitation', file);
    return apiService.request(API_ENDPOINTS.WEDDING_EVENTS.INVITATION_UPLOAD(eventId), {
      method: 'POST',
      body: formData
    });
  }

  async sendInvitations(eventId, guestIds) {
    return apiService.post(API_ENDPOINTS.WEDDING_EVENTS.INVITATION_SEND(eventId), { guestIds });
  }

  async sendReminders(eventId) {
    return apiService.post(API_ENDPOINTS.WEDDING_EVENTS.INVITATION_SEND_REMINDERS(eventId));
  }

  async sendSingleInvitation(eventId, guestId) {
    return apiService.post(API_ENDPOINTS.WEDDING_EVENTS.INVITATION_SEND_SINGLE(eventId, guestId));
  }
}

export default new WeddingEventService();